import { inject, Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { environment } from '../../environments/environment'
import { Observable } from 'rxjs'

/* ── Marketplace M7: reviews (escrow-verified) — hand-written bridge client ── */

/**
 * A verified-buyer review of a SETTLED secure transaction. Field list follows
 * the live swagger (`MarketplaceReview`): admin-side flags (reported/hidden/
 * suspicious) are only meaningful in the admin console response.
 */
export interface MarketplaceReview {
	id?: string
	txId?: string | null
	adId?: string | null
	buyerKcUserId?: string | null
	sellerKcUserId?: string | null
	/** 1..5 — اجباری (خریدار). */
	sellerRating?: number | null
	/** 1..5 — اختیاری (کیفیت کالا). */
	itemRating?: number | null
	comment?: string | null
	/** پاسخ فروشنده. */
	reply?: string | null
	repliedAt?: string | null
	/** نظر ویرایش شده؟ (نمایش «ویرایش‌شده»). */
	edited?: boolean | null
	createdDate?: string | null

	/* Admin-console only flags (live schema). */
	reported?: boolean | null
	reportReason?: string | null
	hidden?: boolean | null
	suspicious?: boolean | null
}

/** خلاصه امتیازهای یک فروشنده — `GET /reviews/seller/{kcUserId}/summary`. */
export interface MarketplaceReviewSummary {
	avgSellerRating?: number | null
	sellerCount?: number | null
	avgItemRating?: number | null
	itemCount?: number | null
	satisfactionPercent?: number | null
	/** false → «داده کافی وجود ندارد» (حداقل تعداد نظر برای درصد رضایت). */
	enoughData?: boolean
}

/** Spring Page<MarketplaceReview> — فقط فیلدهای مصرفی. */
export interface MarketplaceReviewPage {
	content: MarketplaceReview[]
	totalElements: number
	totalPages?: number
	last?: boolean
}

/** بدنه ثبت/ویرایش نظر — sellerRating اجباری، itemRating و comment اختیاری. */
export interface MarketplaceReviewPayload {
	sellerRating: number
	itemRating?: number | null
	comment?: string | null
}

/**
 * Bridge client for the marketplace review endpoints (M7). The generated
 * client doesn't know this controller yet (generate-api pulls the deployed
 * swagger of the older release train) — same hand-written pattern as
 * SecureTransactionService; switch imports after the next API regeneration.
 *
 * Public (guest-safe): GET /reviews/seller/{kcUserId}[?page&size] and /summary.
 * Authenticated: submit (buyer of a SETTLED tx, once), edit/delete (own),
 * reply/report (seller). Admin: page/hide/restore (SUPER_ADMIN …).
 */
@Injectable({ providedIn: 'root' })
export class MarketplaceReviewService {
	private http = inject(HttpClient)
	private base = environment.apiUrl
	private root = `${this.base}/reviews`

	/* ── Public (guest-safe GETs) ──────────────────────────────────────── */

	listForSeller(sellerKcUserId: string, page = 0, size = 5): Observable<MarketplaceReviewPage> {
		return this.http.get<MarketplaceReviewPage>(`${this.root}/seller/${sellerKcUserId}?page=${page}&size=${size}`)
	}

	sellerSummary(sellerKcUserId: string): Observable<MarketplaceReviewSummary> {
		return this.http.get<MarketplaceReviewSummary>(`${this.root}/seller/${sellerKcUserId}/summary`)
	}

	/* ── Buyer (owner of the SETTLED tx) ───────────────────────────────── */

	/** ثبت نظر روی معامله — هر tx فقط یکبار (بک‌اند خطای تکرار می‌دهد). */
	submitForTx(txId: string, payload: MarketplaceReviewPayload): Observable<MarketplaceReview> {
		return this.http.post<MarketplaceReview>(`${this.root}/tx/${txId}`, payload)
	}

	/** ویرایش نظر خودم. */
	update(id: string, payload: MarketplaceReviewPayload): Observable<MarketplaceReview> {
		return this.http.put<MarketplaceReview>(`${this.root}/${id}`, payload)
	}

	/** حذف نظر خودم. */
	remove(id: string): Observable<void> {
		return this.http.delete<void>(`${this.root}/${id}`)
	}

	/* ── Seller ────────────────────────────────────────────────────────── */

	reply(id: string, reply: string): Observable<MarketplaceReview> {
		return this.http.post<MarketplaceReview>(`${this.root}/${id}/reply`, { reply })
	}

	report(id: string, reason: string): Observable<void> {
		return this.http.post<void>(`${this.root}/${id}/report`, { reason })
	}

	/* ── Admin ─────────────────────────────────────────────────────────── */

	adminPage(page = 0, size = 20): Observable<MarketplaceReviewPage> {
		return this.http.get<MarketplaceReviewPage>(`${this.root}/admin?page=${page}&size=${size}`)
	}

	hide(id: string, reason?: string | null): Observable<void> {
		const qs = reason ? `?reason=${encodeURIComponent(reason)}` : ''
		return this.http.post<void>(`${this.root}/admin/${id}/hide${qs}`, {})
	}

	restore(id: string): Observable<void> {
		return this.http.post<void>(`${this.root}/admin/${id}/restore`, {})
	}
}
